import { useId } from 'react';

type IIconProps = { size?: number; className?: string };

const base = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

// Star path is sized so its bounding box visually matches the shield icon
// (both span ~y=2..14 in the 16×16 canvas) — earlier the star was noticeably
// smaller because its path only covered y=2..11.5.
const STAR_PATH = 'M8 2 9.6 6.3 14.2 6.5 10.6 9.3 11.8 13.8 8 11.2 4.2 13.8 5.4 9.3 1.8 6.5 6.4 6.3z';

export function StarOutlineIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <path d={STAR_PATH} />
    </svg>
  );
}

export function StarFilledIcon({ size = 16, className }: IIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      className={className}
      fill="currentColor"
      stroke="none"
    >
      <path d={STAR_PATH} />
    </svg>
  );
}

// Shield path is shared between the outline + filled variants so the two
// glyphs are pixel-aligned. Matches the FilterSidebar Safety section.
const SHIELD_PATH = 'M8 1.8 2.5 4v4.6c0 3.4 2.5 5.4 5.5 6.6 3-1.2 5.5-3.2 5.5-6.6V4z';

export function SafetyShieldIcon({ size = 16, className }: IIconProps) {
  return (
    // Decorative — the wrapping <button>/<span> carries the accessible name.
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base} aria-hidden="true">
      <path d={SHIELD_PATH} />
    </svg>
  );
}

export function SafetyShieldFilledIcon({
  size = 16,
  className,
  gradient,
}: IIconProps & { gradient?: boolean }) {
  const rawId = useId();
  // SVG url(#id) cannot contain ':' characters that React's useId() produces.
  const gradId = rawId.replace(/:/g, '-') + 'safetyShieldGrad';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      className={className}
      fill={gradient ? `url(#${gradId})` : 'currentColor'}
      stroke="none"
      aria-hidden="true"
    >
      {gradient && (
        <defs>
          {/* light-blue → slightly deeper blue, left to right, for a bit of texture */}
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" style={{ stopColor: 'var(--brand-blue-light)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--brand-blue)' }} />
          </linearGradient>
        </defs>
      )}
      <path d={SHIELD_PATH} />
    </svg>
  );
}

export function FlagIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <path d="M3 2v12" />
      <path d="M3 2h8l-2 3.5L13 9H3" />
    </svg>
  );
}

export function StackedFloorsIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <rect x="2" y="2.5" width="12" height="3" rx="0.8" />
      <rect x="2" y="6.5" width="12" height="3" rx="0.8" />
      <rect x="2" y="10.5" width="12" height="3" rx="0.8" />
    </svg>
  );
}

export function FloorPlanIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <rect x="2" y="2" width="12" height="12" rx="1" />
      <path d="M2 7h5v7" />
      <path d="M7 7h7" />
    </svg>
  );
}

export function PersonIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <circle cx="8" cy="5" r="2.5" />
      <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    </svg>
  );
}

export function ChatBubbleIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <path d="M13 2H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h2l2 2.5L9 11h4a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
    </svg>
  );
}

export function LockIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <rect x="3" y="7" width="10" height="7" rx="1" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

/** 3×2 grid of dots for column drag handle. Uses a wider viewBox. */
export function DragHandleDotsIcon({ className }: { className?: string }) {
  return (
    <svg width={16} height={8} viewBox="0 0 16 8" className={className} fill="currentColor">
      <circle cx="3"  cy="2" r="1.2" />
      <circle cx="8"  cy="2" r="1.2" />
      <circle cx="13" cy="2" r="1.2" />
      <circle cx="3"  cy="6" r="1.2" />
      <circle cx="8"  cy="6" r="1.2" />
      <circle cx="13" cy="6" r="1.2" />
    </svg>
  );
}

export function PlusIcon({ size = 16, className }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...base}>
      <line x1="8" y1="3" x2="8" y2="13" />
      <line x1="3" y1="8" x2="13" y2="8" />
    </svg>
  );
}
